/**
 * The needs-input "breathe" (docs/ux/design-language.md §9): a soft glow ring whose opacity moves
 * between 0.55 and 1 over 3 s for as long as an item needs you or is blocked. Full on the
 * top/focused item, faint (38 %) on the others. Opacity only; it pauses off-screen and in hidden
 * tabs (no work for the compositor), and it is static under reduced motion (CSS).
 *
 * The CSS lives in app.css (`.vk-breathe`); this module only marks elements off-screen and the
 * document hidden, through one shared IntersectionObserver.
 */
import { type RefObject, useEffect } from 'react'

let io: IntersectionObserver | undefined
let visibilityInstalled = false

function observer(): IntersectionObserver | undefined {
  if (typeof IntersectionObserver === 'undefined') return undefined
  io ??= new IntersectionObserver((entries) => {
    for (const e of entries) e.target.classList.toggle('is-offscreen', !e.isIntersecting)
  })
  return io
}

/** `<html data-tab-hidden>` while the tab is hidden: every breathe pauses. */
export function installTabVisibility() {
  if (visibilityInstalled || typeof document === 'undefined') return
  visibilityInstalled = true
  const sync = () => {
    if (document.hidden) document.documentElement.dataset.tabHidden = ''
    else delete document.documentElement.dataset.tabHidden
  }
  document.addEventListener('visibilitychange', sync)
  sync()
}

/** Pauses this element's breathe while it is off-screen. */
export function useBreathePause(ref: RefObject<Element | null>, enabled: boolean) {
  useEffect(() => {
    const el = ref.current
    const o = observer()
    if (!enabled || !el || !o) return
    installTabVisibility()
    o.observe(el)
    return () => {
      o.unobserve(el)
      el.classList.remove('is-offscreen')
    }
  }, [ref, enabled])
}

export type BreatheTone = 'wait' | 'blocked'
export type BreatheStrength = 'full' | 'faint'

/** Class names for a breathing element. */
export function breatheClass(tone: BreatheTone | undefined, strength: BreatheStrength): string {
  if (!tone) return ''
  return ` vk-breathe${tone === 'blocked' ? ' is-blocked-glow' : ''}${strength === 'faint' ? ' is-faint' : ''}`
}
